import React, { useEffect, useState } from 'react';

import cn from 'classnames';

import { Icon, IconButton } from 'components';
import Modal from 'components/Modal';
import { ButtonV3 } from 'components/ComponentV2';
import { TextArea } from 'components/form';
import { CompanyAttributes } from 'lib/models/company';
import { useSponsorCompanies } from 'lib/providers/SocietySponsorshipProvider';

import styles from './MessageCompanyModal.module.scss';

interface MessageCompanyModalProps {
  open: boolean;
  onClose: () => void;
  companyData?: CompanyAttributes;
}

export const MessageCompanyModal: React.FC<MessageCompanyModalProps> = (
  props: MessageCompanyModalProps
) => {
  const { open, onClose, companyData } = props;

  const {
    isLoading: sendMessageLoading,
    generateGPTMessage,
    sendMessageToCompany,
  } = useSponsorCompanies();

  const [dots, setDots] = useState('');
  const [message, setMessage] = useState<string>('');
  const [placeholder, setPlaceholder] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isGPTMessage, setIsGPTMessage] = useState<boolean>(false);

  useEffect(() => {
    if (!open) {
      setDots('');
      setMessage('');
      setPlaceholder('');
      setIsLoading(false);
      setIsGPTMessage(false);
    } else {
      generateMessage();
    }
  }, [open]);

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    if (isLoading) {
      interval = setInterval(() => {
        setDots((prevDots) => {
          if (prevDots.length >= 3) {
            return '';
          } else {
            return prevDots + '.';
          }
        });
      }, 500);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isLoading]);

  const generateMessage = async () => {
    setIsLoading(true);
    const response = await generateGPTMessage(companyData?.id || '');
    setIsLoading(false);
    if (response && !message && open) {
      setPlaceholder(response);
      setIsGPTMessage(true);
    } else {
      setIsGPTMessage(false);
    }
  };

  const onUseLetter = () => {
    setMessage(placeholder);
    onDismissLetter();
  };

  const onDismissLetter = () => {
    setPlaceholder('');
    setIsGPTMessage(false);
  };

  const onSendMessage = async () => {
    const response = await sendMessageToCompany(companyData?.id || '', message);
    if (response) {
      onClose();
    }
  };

  return (
    <Modal
      isOpen={open}
      onRequestClose={onClose}
      contentClassName={styles.MessageCompanyModal}
    >
      <div className={styles.MessageCompanyModalHeader}>
        <p>Message {companyData?.name}</p>
        <IconButton
          className={styles.closeIcon}
          iconName="close"
          size="xsmall"
          onClick={() => onClose()}
        />
      </div>
      <div className={styles.MessageCompanyModalContent}>
        <div className={styles.messageBox}>
          {isGPTMessage && (
            <div className={styles.reviewBox}>
              <div className={styles.info}>
                <Icon iconName="icon_info" />
                <p>Auto-generated letter</p>
              </div>
              <div className={styles.action}>
                <ButtonV3 size="small" onClick={onUseLetter}>
                  Use letter
                </ButtonV3>
                <ButtonV3
                  size="small"
                  color="secondary"
                  onClick={onDismissLetter}
                >
                  Dismiss
                </ButtonV3>
              </div>
            </div>
          )}
          <TextArea
            placeholder={
              placeholder || (isLoading && `Generating${dots}`) || ''
            }
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            isValid
            className={cn(styles.textBox, { [styles.minHeight]: isGPTMessage })}
          />
        </div>
      </div>
      <div className={styles.MessageCompanyModalAction}>
        <ButtonV3
          disabled={!message}
          onClick={onSendMessage}
          isLoading={sendMessageLoading}
        >
          Send message
        </ButtonV3>
      </div>
    </Modal>
  );
};
